/**
 * SOWELLBE — comportements globaux.
 *
 * Volontairement minimal : mesure de la hauteur d'en-tête, état de défilement,
 * et apparitions au scroll. Tout le reste vit dans le {% javascript %} de la
 * section concernée. GSAP n'est chargé qu'à la demande (voir theme.liquid).
 */

(() => {
  const root = document.documentElement;

  /* ------------------------------------------------------------------
     Hauteur réelle de l'en-tête (barre d'annonces incluse).
     Sert au hero plein écran et aux éléments collants : sans ça, une
     section en 100svh déborde du premier écran de la hauteur du header.
     ------------------------------------------------------------------ */
  const measureHeader = () => {
    const main = document.getElementById('MainContent');
    if (!main) return;
    const offset = Math.round(main.getBoundingClientRect().top + window.scrollY);
    root.style.setProperty('--header-offset', `${Math.max(offset, 0)}px`);
  };

  const measureWhenIdle = () => {
    if (window.scrollY > 0) return;
    measureHeader();
  };

  measureHeader();
  window.addEventListener('load', measureWhenIdle);
  window.addEventListener('resize', measureWhenIdle);
  document.addEventListener('shopify:section:load', measureWhenIdle);

  /* ------------------------------------------------------------------
     État de défilement — permet aux sections de réagir sans écouteur propre.
     ------------------------------------------------------------------ */
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      root.classList.toggle('is-scrolled', window.scrollY > 40);
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ------------------------------------------------------------------
     Apparitions au défilement.
     ------------------------------------------------------------------ */
  if (!root.classList.contains('reveal-enabled')) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    root.classList.remove('reveal-enabled');
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  const observe = (scope) => {
    for (const element of scope.querySelectorAll('[data-reveal]')) {
      if (element.classList.contains('is-revealed')) continue;
      observer.observe(element);
    }
  };

  observe(document);

  /* Theme Editor : réobserver les sections rechargées à chaud. */
  document.addEventListener('shopify:section:load', (event) => {
    observe(event.target);
  });
})();

/* ==========================================================================
   Acceptation des conditions générales de vente avant le passage en caisse.

   Les extensions d'interface sur les étapes du checkout Shopify sont réservées
   aux boutiques Shopify Plus : le consentement est donc recueilli dans le
   panier, dernière étape que le thème contrôle. Le formulaire porte `novalidate`
   et le panier comporte d'autres boutons de soumission (mise à jour des
   quantités) — la contrainte est donc appliquée ici, et uniquement sur le
   bouton nommé « checkout ».
   ========================================================================== */
(() => {
  const clearError = (terms) => {
    const input = terms.querySelector('[data-cart-terms-input]');
    const error = terms.querySelector('[data-cart-terms-error]');
    terms.classList.remove('cart-terms--invalid');
    if (error) error.hidden = true;
    if (input) input.removeAttribute('aria-invalid');
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!event.submitter || event.submitter.name !== 'checkout') return;

      const terms = form.querySelector('[data-cart-terms]');
      if (!terms) return;

      const input = terms.querySelector('[data-cart-terms-input]');
      if (!input || input.checked) return;

      event.preventDefault();
      event.stopPropagation();

      const error = terms.querySelector('[data-cart-terms-error]');
      terms.classList.add('cart-terms--invalid');
      if (error) error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    },
    true
  );

  /* Dès que la case est cochée, l'erreur disparaît. */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.matches?.('[data-cart-terms-input]') || !input.checked) return;
    const terms = input.closest('[data-cart-terms]');
    if (terms) clearError(terms);
  });
})();

/* ==========================================================================
   Lien permanent « Gérer mes cookies ».

   La CNIL exige que le visiteur puisse revenir sur son choix à tout moment.
   La bannière native Shopify expose `privacyBanner.showPreferences()`, mais le
   script n'est injecté que si la boutique a activé la bannière de consentement :
   le lien reste donc masqué tant que l'API n'a pas répondu présente, plutôt que
   d'afficher un lien sans effet.
   ========================================================================== */
(() => {
  const items = document.querySelectorAll('[data-cookie-preferences-item]');
  if (items.length === 0) return;

  const api = () => window.privacyBanner || window.Shopify?.privacyBanner;

  const reveal = () => {
    if (typeof api()?.showPreferences !== 'function') return false;
    for (const item of items) item.hidden = false;
    return true;
  };

  /* Le script de bannière est chargé de façon asynchrone par Shopify :
     on retente quelques secondes, puis on abandonne silencieusement. */
  if (!reveal()) {
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (reveal() || attempts > 20) clearInterval(timer);
    }, 250);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-cookie-preferences]');
    if (!trigger) return;
    event.preventDefault();
    api()?.showPreferences?.();
  });
})();
